import React, { useState } from 'react';
import { Globe, Plus, Copy, Trash2, ShieldCheck, ShieldAlert, Loader2, ArrowRight, CheckCircle2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge, IconButton, ErrorState, LoadingState, EmptyState } from '@/components/primitives';

const copy = (text, what) => {
  if (!navigator.clipboard) { toast.error('Clipboard is not available in this browser'); return; }
  navigator.clipboard.writeText(text).then(
    () => toast.success(`${what} copied`),
    () => toast.error('Could not copy to the clipboard')
  );
};

/**
 * Ownership proof. The backend releases plaintext credentials only for
 * verified domains, so this panel says so, and shows the exact record to
 * publish (type, host, value) plus the result of the last Verify click.
 */
function VerificationPanel({ domain, check, onVerify }) {
  const v = domain.verification || {};
  const pending = check?.status === 'pending';
  return (
    <div className="ws-verify" aria-labelledby={`verify-title-${domain.id}`}>
      <div className="ws-verify-title" id={`verify-title-${domain.id}`}>
        <ShieldAlert aria-hidden="true" /> Verify that you own this domain
      </div>
      <p className="ws-hint" style={{ color: 'var(--text-secondary)' }}>
        Breach results are collected now, but exposed passwords stay hidden until ownership is proven.
        This stops anyone from monitoring a domain they don't control and reading its staff's credentials.
      </p>
      <dl className="ws-verify-record">
        <dt>Type</dt>
        <dd>{v.record_type || 'TXT'}</dd>
        <dt>Host</dt>
        <dd><code>{v.host || domain.domain}</code></dd>
        <dt>Value</dt>
        <dd>
          <code data-testid={`verify-token-${domain.id}`}>{v.value}</code>
          {v.value && (
            <IconButton label="Copy TXT record value" icon={Copy} size="sm" variant="outline" onClick={() => copy(v.value, 'TXT value')} />
          )}
        </dd>
      </dl>
      {v.help && <p className="ws-hint">{v.help} DNS changes can take a few minutes to propagate.</p>}
      <div className="ws-toolbar">
        <Button size="sm" onClick={() => onVerify(domain)} disabled={pending} data-testid={`verify-domain-${domain.id}`}>
          {pending ? <Loader2 className="is-spinner" aria-hidden="true" /> : <ShieldCheck aria-hidden="true" />}
          {pending ? 'Checking DNS…' : 'Verify'}
        </Button>
        <span role="status" aria-live="polite" className="ws-hint">
          {check?.status === 'not_found' && (
            <>TXT record not found on {domain.domain} yet (checked {check.at.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}). If you just added it, wait a few minutes and verify again.</>
          )}
          {check?.status === 'error' && <span className="ws-error-text">{check.message}</span>}
        </span>
      </div>
    </div>
  );
}

function DomainCard({ domain, check, onVerify, onRemove, onViewEmails }) {
  return (
    <article className="is-card ws-domain-card" data-testid={`domain-card-${domain.id}`}>
      <div className="ws-domain-head">
        <span className="ws-domain-icon" aria-hidden="true"><Globe size={18} /></span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="ws-toolbar" style={{ gap: 8 }}>
            <h3 className="ws-domain-name ws-break">{domain.domain}</h3>
            {domain.verified ? (
              <Badge tone="success" icon={ShieldCheck} data-testid={`domain-verified-${domain.id}`}>Verified</Badge>
            ) : (
              <Badge tone="warning" icon={ShieldAlert} data-testid={`domain-verified-${domain.id}`}>Unverified</Badge>
            )}
          </div>
          <div className="ws-domain-counts">
            <span><b>{domain.email_count || 0}</b> emails</span>
            <span><b className={domain.breach_count > 0 ? 'is-hot' : undefined}>{domain.breach_count || 0}</b> breach results</span>
            {domain.verified && domain.verified_at && (
              <span>Verified {new Date(domain.verified_at).toLocaleDateString()}</span>
            )}
          </div>
        </div>
        <IconButton label={`Stop monitoring ${domain.domain}`} icon={Trash2} variant="danger" onClick={() => onRemove(domain)} />
      </div>
      {!domain.verified && <VerificationPanel domain={domain} check={check} onVerify={onVerify} />}
      <div className="ws-domain-actions">
        <Button variant="outline" size="sm" onClick={() => onViewEmails(domain.domain)}>
          View emails <ArrowRight aria-hidden="true" />
        </Button>
      </div>
    </article>
  );
}

function AddDomainForm({ onAdd, first }) {
  const [value, setValue] = useState('');
  const [autoEnumerate, setAutoEnumerate] = useState(true);
  const [pending, setPending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const domain = value.trim();
    if (!domain || pending) return;
    setPending(true);
    try {
      await onAdd(domain, autoEnumerate);
      setValue('');
    } catch { /* toast shown by the caller */ } finally {
      setPending(false);
    }
  };

  return (
    <form className="is-card ws-add-card" onSubmit={submit} aria-labelledby="add-domain-title">
      <div className="ws-toolbar" style={{ marginBottom: 'var(--space-3)' }}>
        <Plus size={15} aria-hidden="true" style={{ color: 'var(--accent-from)' }} />
        <h3 id="add-domain-title" className="ws-row-title">{first ? 'Add your first domain' : 'Monitor another domain'}</h3>
      </div>
      <div className="ws-add-row">
        <div className="ws-field">
          <label className="ws-label" htmlFor="add-domain-input">Domain</label>
          <Input
            id="add-domain-input"
            className="ws-input-inset ws-mono"
            placeholder="example.com"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            autoComplete="off"
            spellCheck={false}
            inputMode="url"
          />
        </div>
        <Button type="submit" disabled={pending || !value.trim()}>
          {pending ? <Loader2 className="is-spinner" aria-hidden="true" /> : <Plus aria-hidden="true" />}
          Add domain
        </Button>
      </div>
      <label className="ws-check" style={{ marginTop: 'var(--space-3)' }}>
        <input type="checkbox" checked={autoEnumerate} onChange={(e) => setAutoEnumerate(e.target.checked)} />
        <span>Also monitor common role addresses on this domain (info@, admin@, support@, hr@, security@ and similar)</span>
      </label>
    </form>
  );
}

const STEPS = [
  ['Add a domain', 'Common role addresses can be added for you.'],
  ['Publish the TXT record and verify', 'Proves ownership, which unlocks exposed-password details.'],
  ['Review breach results', 'Addresses are checked against breach sources; acknowledge what you have handled.'],
];

export default function DomainList({ domains, verifyChecks, onRetry, onAdd, onVerify, onRemove, onViewEmails }) {
  const list = domains.data?.domains || [];

  if (domains.loading && !domains.data) return <LoadingState label="domains" rows={3} />;
  if (domains.error && !domains.data) {
    return <ErrorState title="Couldn't load your domains" error={domains.error} onRetry={onRetry} />;
  }

  return (
    <div className="ws-stack">
      {domains.error && (
        <ErrorState variant="inline" title="Showing the last loaded domains" error={domains.error} onRetry={onRetry} />
      )}
      {list.length === 0 ? (
        <>
          <EmptyState
            icon={Globe}
            title="No domains monitored yet"
            description="Breach Monitor watches the addresses on domains you own and tells you when they appear in a breach."
          />
          <ol className="ws-stack" style={{ gap: 'var(--space-2)', margin: 0, padding: 0, listStyle: 'none' }} aria-label="Getting started">
            {STEPS.map(([title, desc], i) => (
              <li key={title} className="ws-row">
                <div className="ws-row-main">
                  <Badge tone={i === 0 ? 'accent' : 'neutral'}>{i + 1}</Badge>
                  <div>
                    <div className="ws-row-title">{title}</div>
                    <div className="ws-row-desc">{desc}</div>
                  </div>
                </div>
              </li>
            ))}
          </ol>
        </>
      ) : (
        <>
          <p className="ws-meta">
            {list.length} monitored domain{list.length === 1 ? '' : 's'}
            {list.some((d) => !d.verified) && (
              <> · <AlertTriangle size={12} aria-hidden="true" style={{ display: 'inline', verticalAlign: '-2px', color: 'var(--state-warning)' }} /> {list.filter((d) => !d.verified).length} waiting for verification</>
            )}
            {list.every((d) => d.verified) && (
              <> · <CheckCircle2 size={12} aria-hidden="true" style={{ display: 'inline', verticalAlign: '-2px', color: 'var(--state-success)' }} /> all verified</>
            )}
          </p>
          <div className="ws-domain-grid">
            {list.map((d) => (
              <DomainCard
                key={d.id}
                domain={d}
                check={verifyChecks[d.id]}
                onVerify={onVerify}
                onRemove={onRemove}
                onViewEmails={onViewEmails}
              />
            ))}
          </div>
        </>
      )}
      <AddDomainForm onAdd={onAdd} first={list.length === 0} />
    </div>
  );
}
